import React, { useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { Button } from '@fashionnovateam/nova-ui-toolkit';
import { useRequestedItemsContext } from '../hooks/useRequestedItems';
import {
  useSubmitItemLeadTime,
  useAddItemLeadTimeComment,
} from '../../../Lib/Hooks/item-lead-time.hooks';
import { ItemLeadTimeFormValues } from '../types';
import { ControlledFormInput } from '../../../Lib/Utils/FormUtils/ControlledFormInput';
import { UpdateOneSchema } from '../validation';
import { normalizeSubmissionData } from '../utils/normalizeSubmissionData';
import { getLastComment } from '../utils/commentUtils';
import { getFormDefaults } from './utils/formDefaults';
import { CostsSection } from './sections/CostsSection';
import { LogisticsSection } from './sections/LogisticsSection';
import { StockDetailsSection } from './sections/StockDetailsSection';
import { ExclusivitySection } from './sections/ExclusivitySection';

export function QuoteTab() {
  const { selectedItem, userId, isCompleted, closeDrawer, perms } =
    useRequestedItemsContext();
  const { mutate: submitItem, isLoading: isSubmitting } =
    useSubmitItemLeadTime();
  const { mutate: addComment, isLoading: isAddingComment } =
    useAddItemLeadTimeComment();

  const canEditCommentsOnCompleted = isCompleted && perms.canUpdate;
  const isFormDisabled = isCompleted || !perms.canUpdate;
  const isCommentsDisabled = isFormDisabled && !canEditCommentsOnCompleted;

  const defaultValues = useMemo(
    () => getFormDefaults(selectedItem),
    [selectedItem],
  );

  const methods = useForm<ItemLeadTimeFormValues>({
    defaultValues,
    resolver: joiResolver(UpdateOneSchema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = async (data: ItemLeadTimeFormValues) => {
    if (!selectedItem || !userId) return;

    if (canEditCommentsOnCompleted) {
      const commentValue = data.comments?.trim() || '';
      if (!commentValue) {
        return;
      }
      addComment(
        {
          userId,
          data: {
            worksheet_id: selectedItem.worksheet_id,
            comments: commentValue,
          },
        },
        {
          onSuccess: () => {
            closeDrawer();
          },
        },
      );
      return;
    }

    if (!perms.canSubmit) return;

    const formattedData = normalizeSubmissionData({
      cost_jr: Number(data.cost_jr) || 0,
      cost_plus: Number(data.cost_plus) || 0,
      country_of_origin: data.country_of_origin || '',
      shipment_mode: data.shipment_mode || '',
      exclusivity: data.exclusivity || 'Not Exclusive',
      earliest_custom_cut_delivery_date:
        data.earliest_custom_cut_delivery_date || '',
      custom_cut_lead_time_in_weeks:
        Number(data.custom_cut_lead_time_in_weeks) || 0,
      custom_MOQ_jr: Number(data.custom_MOQ_jr) || 0,
      custom_MOQ_plus: Number(data.custom_MOQ_plus) || 0,
      stock_quantity: Number(data.stock_quantity) || 0,
      stock_ratio: data.stock_ratio || '',
      earliest_IHD_date_to_deliver_stock:
        data.earliest_IHD_date_to_deliver_stock || '',
      incoming_stock_quantity: Number(data.incoming_stock_quantity) || 0,
      incoming_stock_ratio: data.incoming_stock_ratio || '',
      incoming_stock_date: data.incoming_stock_date || '',
      comments: data.comments || '',
    });

    submitItem(
      {
        userId,
        worksheetId: selectedItem.worksheet_id,
        data: formattedData,
      },
      {
        onSuccess: () => {
          closeDrawer();
        },
      },
    );
  };

  const commentsValue = watch('comments');

  const isCommentUnchanged =
    (commentsValue?.trim() ?? '') === getLastComment(selectedItem?.comments);

  const isSaveDisabled = isCompleted
    ? !canEditCommentsOnCompleted ||
      isAddingComment ||
      !commentsValue?.trim() ||
      isCommentUnchanged
    : !perms.canSubmit || isSubmitting || !isValid;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex-1 flex flex-col overflow-hidden"
      >
        <div className="flex-1 overflow-auto p-4 space-y-4">
          <CostsSection disabled={isFormDisabled} />
          <LogisticsSection disabled={isFormDisabled} />
          <StockDetailsSection disabled={isFormDisabled} />
          <ExclusivitySection disabled={isFormDisabled} />

          <div className="space-y-2">
            <ControlledFormInput
              control={control}
              name="comments"
              label="Comments"
              inputType="textarea"
              disabled={isCommentsDisabled}
              inputProps={{
                rows: 4,
              }}
            />
          </div>

          {(perms.canSubmit && !isCompleted) || canEditCommentsOnCompleted ? (
            <div className="pb-4">
              <Button
                type="submit"
                disabled={isSaveDisabled}
                className="w-full"
              >
                {(() => {
                  if (isSubmitting || isAddingComment) return 'Saving...';
                  if (canEditCommentsOnCompleted) return 'Save Comment';
                  return 'Save';
                })()}
              </Button>
            </div>
          ) : null}
        </div>
      </form>
    </FormProvider>
  );
}
